import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { createSlug } from "./utils.js";
import axios from "axios";
import axiosInstance from "../Backend/IghotaVirtualLearning/axiosInstance.js";

// Cache configuration
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const getCachedData = (key) => {
	const cached = cache.get(key);
	if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
		return cached.data;
	}
	return null;
};

// Utility function for retrying API calls
const retryApiCall = async ({
	apiCall,
	retryCount = 2,
	initialDelay = 5000,
	timeout = 15000,
	onRetry = () => {},
	validateResponse = () => true,
}) => {
	let attempt = 0;
	const initialDelayValue = initialDelay;

	while (attempt <= retryCount) {
		const currentDelay = initialDelayValue * Math.pow(2, attempt);

		try {
			const response = await Promise.race([
				apiCall(),
				new Promise((_, reject) =>
					setTimeout(
						() => reject(new Error("Request timeout")),
						timeout
					)
				),
			]);

			if (response.status === 200) {
				if (validateResponse(response)) {
					return response;
				}
				throw new Error("Invalid response data");
			}

			throw new Error(`Received status code: ${response.status}`);
		} catch (err) {
			if (axios.isCancel(err)) {
				throw err;
			}

			const status = err?.response?.status;

			if (status && status >= 400 && status < 500 && status !== 408) {
				const error = new Error(`Client error: ${status}`);
				error.attempts = attempt + 1;
				error.originalError = err;
				throw error;
			}

			attempt++;

			if (attempt > retryCount) {
				const error = new Error(`Failed after ${attempt} attempts`);
				error.attempts = attempt;
				error.originalError = err;
				throw error;
			}

			const shouldRetry =
				status >= 500 ||
				status === 408 ||
				err.code === "ECONNABORTED" ||
				err.message === "Request timeout" ||
				!status;

			if (!shouldRetry) {
				const error = new Error("Non-retriable error");
				error.attempts = attempt;
				error.originalError = err;
				throw error;
			}

			onRetry({
				attempt,
				delay: currentDelay,
				error: err,
			});

			await new Promise((resolve) => setTimeout(resolve, currentDelay));
		}
	}
};

export const useFetchStrapiData = () => {
	const { subjectSlug, courseSlug, lessonId } = useParams();
	const mountedRef = useRef(true);
	const cancelSourceRef = useRef(null);
	const requestInProgressRef = useRef(false);
	const previousPathRef = useRef("");
	const [state, setState] = useState({
		strapiData: null,
		isLoading: true,
		error: null,
	});

	useEffect(() => {
		const currentPath = `${subjectSlug}/${courseSlug}/${lessonId}`;

		// If the path has changed, reset cache and state
		if (
			previousPathRef.current &&
			previousPathRef.current !== currentPath
		) {
			cache.clear(); // Optional: if you want to clear cache entirely
			setState({
				strapiData: null,
				isLoading: true,
				error: null,
			});
		}

		previousPathRef.current = currentPath;

		const fetchData = async () => {
			if (cancelSourceRef.current) {
				cancelSourceRef.current.cancel("New request started");
				cancelSourceRef.current = null;
			}

			try {
				// Updated to include lessonId in cache key
				const cacheKey = `${subjectSlug}-${courseSlug}-${lessonId}`;
				const cachedData = getCachedData(cacheKey);

				if (cachedData) {
					if (mountedRef.current) {
						setState({
							strapiData: cachedData,
							isLoading: false,
							error: null,
						});
					}
					return;
				}

				if (!subjectSlug || !courseSlug) {
					if (mountedRef.current) {
						setState((prev) => ({
							...prev,
							isLoading: false,
							error: "Invalid URL parameters",
						}));
					}
					return;
				}

				cancelSourceRef.current = axios.CancelToken.source();
				requestInProgressRef.current = true;

				const response = await retryApiCall({
					apiCall: async () => {
						if (!mountedRef.current) {
							throw new Error("Component unmounted");
						}

						const LESSON_ENDPOINT_URL = `/courses?filters[title][$eqi]=${encodeURIComponent(courseSlug)}&populate=units.lessons.quiz_question.question_option&pagination[limit]=-1`;

						return axiosInstance.get(LESSON_ENDPOINT_URL, {
							cancelToken: cancelSourceRef.current.token,
						});
					},
					validateResponse: (response) =>
						response?.data?.data?.[0] != null,
					onRetry: ({ attempt, delay, error }) => {
						if (mountedRef.current) {
							console.log(
								`Attempt ${attempt} failed. Retrying in ${delay}ms...`,
								error
							);
							Sentry.captureException(error, {
								extra: {
									subjectSlug,
									courseSlug,
									lessonId,
									attempt,
									delay,
								},
							});
						}
					},
				});

				if (!mountedRef.current) return;

				const courseData = response?.data?.data[0];

				if (!courseData) {
					throw new Error("No course data found");
				}

				const processedData = {
					course: courseData?.title || "Unknown Course",
					id: courseData?.id,
					slug: createSlug(courseData?.title || ""),
					units:
						courseData?.units?.map((unit) => ({
							id: unit.id,
							title: unit.title || "Untitled Unit",
							slug: createSlug(unit.title || ""),
							lessons:
								unit?.lessons?.map((lesson) => ({
									id: lesson.id,
									title: lesson.title || "Untitled Lesson",
									slug: createSlug(lesson.title || ""),
									path: `/courses/${subjectSlug}/${courseSlug}/${unit.slug || ""}/${lesson.slug || ""}/${lesson.id}`,
									videoUrl: lesson.videoUrl,
									questions:
										lesson.quiz_question?.map(
											(question) => ({
												id: question.id,
												title: question.title,
												correctAnswer:
													question.correctAnswer,
												options:
													question.question_option?.map(
														(option) => ({
															id: option.id,
															label: option.label,
															answer: option.answer,
															isCorrect:
																option.isCorrect,
															explanation:
																option.explanation ||
																"No explanation provided.",
														})
													) || [],
											})
										) || [],
								})) || [],
						})) || [],
				};

				if (mountedRef.current) {
					setState({
						strapiData: processedData,
						isLoading: false,
						error: null,
					});

					// Save new data in cache with full key including lessonId
					cache.set(cacheKey, {
						data: processedData,
						timestamp: Date.now(),
					});
				}
			} catch (err) {
				if (axios.isCancel(err)) {
					console.log("Request cancelled:", err.message);
					if (
						mountedRef.current &&
						err.message !== "Component unmounted"
					) {
						setState((prev) => ({
							...prev,
							isLoading: false,
							error: "Request cancelled",
						}));
					}
					return;
				}

				if (mountedRef.current) {
					console.error("Fetch error:", err);
					setState({
						strapiData: null,
						isLoading: false,
						error: err.message || "Failed to load data",
					});
					Sentry.captureException(err.originalError || err, {
						extra: {
							subjectSlug,
							courseSlug,
							lessonId,
							attempts: err.attempts || 1,
						},
					});
				}
			} finally {
				requestInProgressRef.current = false;
			}
		};

		fetchData();

		return () => {
			mountedRef.current = false;
			if (cancelSourceRef.current) {
				cancelSourceRef.current.cancel("Component unmounted");
				cancelSourceRef.current = null;
			}
		};
	}, [subjectSlug, courseSlug, lessonId]);

	return {
		quizData: state.strapiData,
		isLoading: state.isLoading,
		error: state.error,
	};
};
